import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../App";

/**
 * Shared offer/interest submission logic (WhatsApp quick auth + /api/quick-offer).
 * Used by both the small QuickOfferPopup and the larger ProductDetailsPopup so
 * the identity/registration flow never has to be duplicated or shown twice.
 */
export function useQuickOffer({ product, isOpen, onSuccess, onClose }) {
  const { user, completeGoogleLogin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingWhatsapp, setCheckingWhatsapp] = useState(false);

  // Already-logged-in users with a WhatsApp number on file skip re-entering it
  const sessionWhatsapp = user?.whatsapp ? user.whatsapp.replace(/\D/g, "").slice(-8) : null;

  const [whatsapp, setWhatsapp] = useState("");
  const [username, setUsername] = useState("");
  const [confirmWhatsappChecked, setConfirmWhatsappChecked] = useState(false);
  const [userExists, setUserExists] = useState(null);
  const [maskedUsername, setMaskedUsername] = useState("");
  const [whatsappError, setWhatsappError] = useState("");

  // Reset identity form whenever the popup opens
  useEffect(() => {
    if (isOpen) {
      setUsername("");
      setConfirmWhatsappChecked(false);
      setWhatsappError("");
      if (sessionWhatsapp) {
        setWhatsapp(sessionWhatsapp);
        setUserExists(true);
        setMaskedUsername(user?.name || user?.username || "");
      } else {
        setWhatsapp("");
        setUserExists(null);
        setMaskedUsername("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sessionWhatsapp]);

  // Check WhatsApp when 8 digits entered (skip for already-logged-in users)
  useEffect(() => {
    if (sessionWhatsapp) return;
    const checkWhatsapp = async () => {
      if (whatsapp.length === 8 && /^6\d{7}$/.test(whatsapp)) {
        setCheckingWhatsapp(true);
        setWhatsappError("");
        try {
          const response = await axios.post(`${API}/auth/check-whatsapp`, { whatsapp });
          setUserExists(response.data.exists);
          if (response.data.exists) {
            setMaskedUsername(response.data.username_masked);
          }
        } catch (error) {
          console.error("Error checking WhatsApp:", error);
        } finally {
          setCheckingWhatsapp(false);
        }
      } else if (whatsapp.length === 8 && !whatsapp.startsWith('6')) {
        setWhatsappError("El número debe iniciar con 6");
        setUserExists(null);
      } else {
        setUserExists(null);
        setMaskedUsername("");
      }
    };
    const debounce = setTimeout(checkWhatsapp, 300);
    return () => clearTimeout(debounce);
  }, [whatsapp, sessionWhatsapp]);

  const identityReady = whatsapp.length === 8 && !(userExists === false && (!username || username.length < 3 || !confirmWhatsappChecked));

  /** mode: "offer" (bid on auctions) | "direct_purchase" (buy-now / interest request) */
  const submitOffer = async ({ mode, amount }) => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Ingresa un monto válido");
      return false;
    }
    if (whatsapp.length !== 8) {
      toast.error("Ingresa los 8 dígitos de tu WhatsApp");
      return false;
    }
    if (!whatsapp.startsWith('6')) {
      toast.error("El número debe iniciar con 6");
      return false;
    }
    if (userExists === false) {
      if (!username || username.length < 3) {
        toast.error("El usuario debe tener al menos 3 caracteres");
        return false;
      }
      if (!confirmWhatsappChecked) {
        toast.error("Confirma que revisaste tu número de WhatsApp");
        return false;
      }
    }
    if (product?.sale_type === "auction" && mode !== "direct_purchase") {
      const currentBid = product.current_bid || product.price;
      if (parseFloat(amount) <= currentBid) {
        toast.error(`La oferta debe ser mayor a $${currentBid}`);
        return false;
      }
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/quick-offer`, {
        product_id: product.product_id,
        amount: parseFloat(amount),
        whatsapp,
        username: userExists === false ? username : null,
        whatsapp_confirm: userExists === false ? whatsapp : null,
        is_new_user: userExists === false,
        mode
      });

      if (response.data.token) {
        completeGoogleLogin(response.data.user, response.data.token);
      }

      toast.success(response.data.message);
      if (response.data.is_new_registration) {
        toast.success("¡Bienvenido a Clueer! Tu usuario y contraseña fueron registrados.", { duration: 5000 });
      }

      onSuccess?.({ ...response.data, amount: parseFloat(amount), mode });
      onClose?.();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar solicitud");
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading, checkingWhatsapp, sessionWhatsapp,
    whatsapp, setWhatsapp, username, setUsername,
    confirmWhatsappChecked, setConfirmWhatsappChecked,
    userExists, maskedUsername, whatsappError,
    identityReady, submitOffer
  };
}
